import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Users, Target, CheckCircle, Clock, Star, Search, ChevronRight, ChevronLeft, Play, Award, Filter } from 'lucide-react';
import { Button } from '../../ui/Button';
export function TrainingApp() {
    const [step, setStep] = useState('department');
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterDifficulty, setFilterDifficulty] = useState('all');
    const [expandedModule, setExpandedModule] = useState(null);
    const departments = [
        {
            id: 'sales',
            name: 'Sales',
            icon: _jsx(Target, { className: "w-6 h-6" }),
            color: 'bg-blue-500',
            description: 'Sales processes, CRM, and client management'
        },
        {
            id: 'operations',
            name: 'Operations',
            icon: _jsx(Users, { className: "w-6 h-6" }),
            color: 'bg-green-500',
            description: 'Daily operations, workflows, and systems'
        },
        {
            id: 'finance',
            name: 'Finance',
            icon: _jsx(Award, { className: "w-6 h-6" }),
            color: 'bg-yellow-500',
            description: 'Accounting, budgeting, and financial reporting'
        },
        {
            id: 'marketing',
            name: 'Marketing',
            icon: _jsx(Star, { className: "w-6 h-6" }),
            color: 'bg-purple-500',
            description: 'Campaign management and brand strategy'
        },
        {
            id: 'hr',
            name: 'Human Resources',
            icon: _jsx(Users, { className: "w-6 h-6" }),
            color: 'bg-red-500',
            description: 'Recruitment, compliance, and employee relations'
        },
        {
            id: 'it',
            name: 'IT & Technology',
            icon: _jsx(BookOpen, { className: "w-6 h-6" }),
            color: 'bg-indigo-500',
            description: 'Systems, security, and technical support'
        }
    ];
    const positionsByDepartment = {
        sales: [
            { id: 'rep', name: 'Sales Representative', description: 'Client interaction and deal closing' },
            { id: 'manager', name: 'Sales Manager', description: 'Team leadership and performance' },
            { id: 'director', name: 'Sales Director', description: 'Strategic planning and forecasting' }
        ],
        operations: [
            { id: 'coordinator', name: 'Operations Coordinator', description: 'Day-to-day task management' },
            { id: 'manager', name: 'Operations Manager', description: 'Process optimization and team coordination' },
            { id: 'director', name: 'Operations Director', description: 'Strategic operations planning' }
        ],
        finance: [
            { id: 'analyst', name: 'Finance Analyst', description: 'Data analysis and reporting' },
            { id: 'manager', name: 'Finance Manager', description: 'Budget management and financial planning' },
            { id: 'director', name: 'CFO', description: 'Strategic financial leadership' }
        ],
        marketing: [
            { id: 'specialist', name: 'Marketing Specialist', description: 'Campaign execution and content' },
            { id: 'manager', name: 'Marketing Manager', description: 'Campaign planning and team management' },
            { id: 'director', name: 'Marketing Director', description: 'Brand strategy and market analysis' }
        ],
        hr: [
            { id: 'specialist', name: 'HR Specialist', description: 'Recruitment and employee support' },
            { id: 'manager', name: 'HR Manager', description: 'Department management and compliance' },
            { id: 'director', name: 'HR Director', description: 'Organizational strategy and culture' }
        ],
        it: [
            { id: 'support', name: 'IT Support', description: 'User support and troubleshooting' },
            { id: 'manager', name: 'IT Manager', description: 'Infrastructure and team management' },
            { id: 'director', name: 'CTO', description: 'Technology strategy and innovation' }
        ]
    };
    const trainingModules = [
        // Sales modules
        {
            id: 'sales-crm-intro',
            title: 'CRM System Fundamentals',
            description: 'Learn the basics of our CRM system and how to use it for client management',
            duration: 45,
            department: 'sales',
            positions: ['rep', 'manager', 'director'],
            category: 'core',
            difficulty: 'beginner',
            completed: true,
            progress: 100,
            lessons: 5
        },
        {
            id: 'sales-process',
            title: 'Sales Process & Pipeline',
            description: 'Master our sales methodology and pipeline stages',
            duration: 60,
            department: 'sales',
            positions: ['rep', 'manager'],
            category: 'core',
            difficulty: 'beginner',
            completed: true,
            progress: 100,
            lessons: 6
        },
        {
            id: 'sales-advanced-closing',
            title: 'Advanced Closing Techniques',
            description: 'Expert techniques for closing high-value deals',
            duration: 90,
            department: 'sales',
            positions: ['rep'],
            category: 'advanced',
            difficulty: 'advanced',
            completed: false,
            progress: 40,
            lessons: 8
        },
        {
            id: 'sales-management',
            title: 'Sales Team Leadership',
            description: 'Managing sales teams, forecasting, and performance metrics',
            duration: 120,
            department: 'sales',
            positions: ['manager', 'director'],
            category: 'leadership',
            difficulty: 'intermediate',
            completed: false,
            progress: 0,
            lessons: 10
        },
        // Operations modules
        {
            id: 'ops-workflows',
            title: 'Standard Operating Procedures',
            description: 'Overview of daily operations and documented workflows',
            duration: 30,
            department: 'operations',
            positions: ['coordinator', 'manager', 'director'],
            category: 'core',
            difficulty: 'beginner',
            completed: true,
            progress: 100,
            lessons: 4
        },
        {
            id: 'ops-project-mgmt',
            title: 'Project Management Basics',
            description: 'Learn to manage projects and track deliverables',
            duration: 75,
            department: 'operations',
            positions: ['coordinator'],
            category: 'core',
            difficulty: 'beginner',
            completed: false,
            progress: 60,
            lessons: 7
        },
        {
            id: 'ops-process-optimization',
            title: 'Process Optimization & Efficiency',
            description: 'Identifying bottlenecks and improving operational efficiency',
            duration: 90,
            department: 'operations',
            positions: ['manager', 'director'],
            category: 'advanced',
            difficulty: 'intermediate',
            completed: false,
            progress: 0,
            lessons: 8
        },
        // Finance modules
        {
            id: 'fin-accounting-101',
            title: 'Accounting Fundamentals',
            description: 'Basic accounting principles and our chart of accounts',
            duration: 120,
            department: 'finance',
            positions: ['analyst', 'manager'],
            category: 'core',
            difficulty: 'beginner',
            completed: false,
            progress: 30,
            lessons: 10
        },
        {
            id: 'fin-budgeting',
            title: 'Budgeting & Financial Planning',
            description: 'Create and manage budgets, forecast financials',
            duration: 100,
            department: 'finance',
            positions: ['manager', 'director'],
            category: 'core',
            difficulty: 'intermediate',
            completed: false,
            progress: 0,
            lessons: 9
        },
        // Marketing modules
        {
            id: 'mkt-campaign',
            title: 'Campaign Planning & Execution',
            description: 'From ideation to launch and analysis',
            duration: 80,
            department: 'marketing',
            positions: ['specialist', 'manager'],
            category: 'core',
            difficulty: 'intermediate',
            completed: false,
            progress: 0,
            lessons: 7
        },
        {
            id: 'mkt-analytics',
            title: 'Marketing Analytics & ROI',
            description: 'Measure campaign performance and optimize spend',
            duration: 70,
            department: 'marketing',
            positions: ['specialist', 'manager', 'director'],
            category: 'core',
            difficulty: 'intermediate',
            completed: false,
            progress: 0,
            lessons: 6
        },
        // HR modules
        {
            id: 'hr-onboarding',
            title: 'Employee Onboarding Process',
            description: 'Guide new hires through our onboarding workflow',
            duration: 50,
            department: 'hr',
            positions: ['specialist', 'manager'],
            category: 'core',
            difficulty: 'beginner',
            completed: true,
            progress: 100,
            lessons: 5
        },
        {
            id: 'hr-compliance',
            title: 'Compliance & Legal Requirements',
            description: 'Employment law, documentation, and company policies',
            duration: 110,
            department: 'hr',
            positions: ['specialist', 'manager', 'director'],
            category: 'core',
            difficulty: 'intermediate',
            completed: false,
            progress: 20,
            lessons: 9
        }
    ];
    const getPositions = () => {
        if (!selectedDepartment)
            return [];
        return positionsByDepartment[selectedDepartment.id] || [];
    };
    const getRelevantModules = () => {
        let modules = trainingModules.filter(m => m.department === selectedDepartment?.id);
        if (selectedPosition) {
            modules = modules.filter(m => m.positions.includes(selectedPosition.id));
        }
        if (searchQuery) {
            modules = modules.filter(m => m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                m.description.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        if (filterDifficulty !== 'all') {
            modules = modules.filter(m => m.difficulty === filterDifficulty);
        }
        return modules;
    };
    const completedCount = getRelevantModules().filter(m => m.completed).length;
    const totalCount = getRelevantModules().length;
    const handleDepartmentSelect = (dept) => {
        setSelectedDepartment(dept);
        setSelectedPosition(null);
        setStep('position');
    };
    const handlePositionSelect = (pos) => {
        setSelectedPosition(pos);
        setStep('modules');
    };
    const handleBackToPosition = () => {
        setSelectedPosition(null);
        setStep('position');
    };
    const handleBackToDepartment = () => {
        setSelectedDepartment(null);
        setSelectedPosition(null);
        setStep('department');
    };
    return (_jsxs("div", { className: "flex flex-col h-full bg-gray-50", children: [_jsx("div", { className: "bg-white border-b border-gray-200 px-6 py-4", children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsxs("h1", { className: "text-2xl font-bold text-gray-900 flex items-center gap-2", children: [_jsx(BookOpen, { className: "w-7 h-7 text-blue-600" }), "Training Center"] }), step !== 'department' && (_jsxs("p", { className: "text-sm text-gray-600 mt-1", children: [selectedDepartment?.name, selectedPosition && ` > ${selectedPosition.name}`] }))] }), step !== 'department' && (_jsxs(Button, { variant: "outline", onClick: step === 'position' ? handleBackToDepartment : handleBackToPosition, className: "flex items-center gap-2", children: [_jsx(ChevronLeft, { className: "w-4 h-4" }), "Back"] }))] }) }), step === 'department' && (_jsx("div", { className: "flex-1 overflow-y-auto p-6", children: _jsxs("div", { className: "max-w-6xl mx-auto", children: [_jsx("h2", { className: "text-xl font-semibold text-gray-900 mb-6", children: "Select Your Department" }), _jsx("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4", children: departments.map((dept) => (_jsxs(motion.button, { onClick: () => handleDepartmentSelect(dept), className: "text-left p-6 bg-white rounded-lg border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all", whileHover: { y: -4 }, transition: { type: 'spring', stiffness: 400, damping: 25 }, children: [_jsx("div", { className: `${dept.color} w-12 h-12 rounded-lg flex items-center justify-center text-white mb-4`, children: dept.icon }), _jsx("h3", { className: "text-lg font-semibold text-gray-900 mb-2", children: dept.name }), _jsx("p", { className: "text-sm text-gray-600 mb-4", children: dept.description }), _jsxs("div", { className: "flex items-center gap-2 text-blue-600 text-sm font-medium", children: ["Get Started", _jsx(ChevronRight, { className: "w-4 h-4" })] })] }, dept.id))) })] }) })), step === 'position' && selectedDepartment && (_jsx("div", { className: "flex-1 overflow-y-auto p-6", children: _jsxs("div", { className: "max-w-4xl mx-auto", children: [_jsx("h2", { className: "text-xl font-semibold text-gray-900 mb-6", children: "Select Your Position" }), _jsx("div", { className: "space-y-3", children: getPositions().map((pos) => (_jsxs(motion.button, { onClick: () => handlePositionSelect(pos), className: "w-full text-left p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all flex items-center justify-between group", whileHover: { x: 4 }, transition: { type: 'spring', stiffness: 400, damping: 25 }, children: [_jsxs("div", { children: [_jsx("h3", { className: "text-base font-semibold text-gray-900 group-hover:text-blue-900", children: pos.name }), _jsx("p", { className: "text-sm text-gray-600", children: pos.description })] }), _jsx(ChevronRight, { className: "w-5 h-5 text-gray-400 group-hover:text-blue-600" })] }, pos.id))) })] }) })), step === 'modules' && selectedDepartment && selectedPosition && (_jsx("div", { className: "flex-1 overflow-y-auto p-6", children: _jsxs("div", { className: "max-w-5xl mx-auto", children: [_jsxs("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4 mb-6", children: [_jsx("div", { className: "bg-white rounded-lg p-4 border border-gray-200", children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("p", { className: "text-sm text-gray-600", children: "Progress" }), _jsxs("p", { className: "text-2xl font-bold text-gray-900", children: [completedCount, "/", totalCount] })] }), _jsx(Award, { className: "w-8 h-8 text-yellow-500" })] }) }), _jsx("div", { className: "bg-white rounded-lg p-4 border border-gray-200", children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("p", { className: "text-sm text-gray-600", children: "Completion Rate" }), _jsxs("p", { className: "text-2xl font-bold text-gray-900", children: [totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0, "%"] })] }), _jsx("div", { className: "w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center", children: _jsxs("span", { className: "text-sm font-semibold text-blue-600", children: [totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0, "%"] }) })] }) }), _jsx("div", { className: "bg-white rounded-lg p-4 border border-gray-200", children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("p", { className: "text-sm text-gray-600", children: "Total Hours" }), _jsxs("p", { className: "text-2xl font-bold text-gray-900", children: [Math.round(getRelevantModules().reduce((sum, m) => sum + m.duration, 0) / 60), "h"] })] }), _jsx(Clock, { className: "w-8 h-8 text-blue-500" })] }) })] }), _jsx("div", { className: "bg-white rounded-lg border border-gray-200 p-4 mb-6", children: _jsxs("div", { className: "flex flex-col md:flex-row gap-4", children: [_jsxs("div", { className: "flex-1 relative", children: [_jsx(Search, { className: "absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" }), _jsx("input", { type: "text", placeholder: "Search modules...", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), className: "w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Filter, { className: "w-4 h-4 text-gray-600" }), _jsxs("select", { value: filterDifficulty, onChange: (e) => setFilterDifficulty(e.target.value), className: "px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm", children: [_jsx("option", { value: "all", children: "All Levels" }), _jsx("option", { value: "beginner", children: "Beginner" }), _jsx("option", { value: "intermediate", children: "Intermediate" }), _jsx("option", { value: "advanced", children: "Advanced" })] })] })] }) }), getRelevantModules().length > 0 ? (_jsx("div", { className: "space-y-4", children: getRelevantModules().map((module) => (_jsxs(motion.div, { className: "bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow", layout: true, children: [_jsxs("button", { onClick: () => setExpandedModule(expandedModule === module.id ? null : module.id), className: "w-full text-left p-4 flex items-start gap-4 hover:bg-gray-50 transition-colors", children: [_jsx("div", { className: "flex-shrink-0 w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center text-white", children: module.completed ? (_jsx(CheckCircle, { className: "w-8 h-8" })) : (_jsx(Play, { className: "w-8 h-8" })) }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsxs("div", { className: "flex items-start justify-between mb-2", children: [_jsxs("div", { children: [_jsx("h3", { className: "text-base font-semibold text-gray-900", children: module.title }), _jsx("p", { className: "text-sm text-gray-600 mt-1", children: module.description })] }), module.completed && (_jsx("span", { className: "ml-2 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex-shrink-0", children: "Completed" }))] }), _jsxs("div", { className: "mt-3 flex items-center gap-4", children: [_jsxs("div", { className: "flex-1 max-w-xs", children: [_jsx("div", { className: "h-2 bg-gray-200 rounded-full overflow-hidden", children: _jsx(motion.div, { className: "h-full bg-blue-600 rounded-full", initial: { width: 0 }, animate: { width: `${module.progress}%` }, transition: { duration: 0.5 } }) }), _jsxs("p", { className: "text-xs text-gray-600 mt-1", children: [module.progress, "% \u2022 ", module.lessons, " lessons"] })] }), _jsxs("div", { className: "flex items-center gap-3 text-xs text-gray-600", children: [_jsxs("span", { className: "flex items-center gap-1", children: [_jsx(Clock, { className: "w-3 h-3" }), module.duration, "m"] }), _jsx("span", { className: `px-2 py-1 rounded-full text-xs font-medium ${module.difficulty === 'beginner'
                                                                            ? 'bg-green-100 text-green-700'
                                                                            : module.difficulty === 'intermediate'
                                                                                ? 'bg-yellow-100 text-yellow-700'
                                                                                : 'bg-red-100 text-red-700'}`, children: module.difficulty.charAt(0).toUpperCase() + module.difficulty.slice(1) })] })] })] }), _jsx(ChevronRight, { className: `w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${expandedModule === module.id ? 'rotate-90' : ''}` })] }), expandedModule === module.id && (_jsx(motion.div, { className: "px-4 pb-4 border-t border-gray-200", initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.2 }, children: _jsxs("div", { className: "pt-4 space-y-3", children: [_jsxs("div", { children: [_jsx("h4", { className: "text-sm font-semibold text-gray-900 mb-2", children: "Course Details" }), _jsx("p", { className: "text-sm text-gray-600", children: module.description })] }), module.progress > 0 && module.progress < 100 && (_jsx("div", { children: _jsxs("p", { className: "text-sm text-gray-600 mb-2", children: ["Continue from lesson ", Math.ceil((module.lessons * module.progress) / 100)] }) })), _jsxs("div", { className: "flex gap-3", children: [_jsxs(Button, { className: "flex-1 flex items-center justify-center gap-2", children: [_jsx(Play, { className: "w-4 h-4" }), module.progress > 0 && module.progress < 100
                                                                    ? 'Continue'
                                                                    : module.completed
                                                                        ? 'Review'
                                                                        : 'Start'] }), module.completed && (_jsxs(Button, { variant: "outline", className: "flex-1", children: [_jsx(Star, { className: "w-4 h-4 mr-2" }), "Bookmark"] }))] })] }) }))] }, module.id))) })) : (_jsxs("div", { className: "text-center py-12", children: [_jsx(BookOpen, { className: "w-12 h-12 text-gray-300 mx-auto mb-4" }), _jsx("p", { className: "text-gray-600", children: "No training modules found matching your criteria" })] }))] }) }))] }));
}
